import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Pencil, Trash2, Search, Package, ImagePlus, X, Image, Video, Loader2, Check, Upload } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import api, { formatCurrency } from '../../lib/api';
import { toast } from 'sonner';

// Simple inline image upload component to avoid React DOM conflicts
const ImageUploadSlot = ({ imageUrl, onUpload, onRemove, slotId }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [localPreview, setLocalPreview] = useState(imageUrl);
  const fileInputRef = useRef(null);

  // Update local preview when imageUrl changes from parent
  useEffect(() => {
    setLocalPreview(imageUrl);
  }, [imageUrl]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 10MB');
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Tipo de arquivo não suportado');
      return;
    }

    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setLocalPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload to Cloudinary
    setUploading(true);
    setProgress(10);

    try {
      const sigResponse = await api.get('/cloudinary/signature?resource_type=image&folder=products');
      const { signature, timestamp, cloud_name, api_key, folder } = sigResponse.data;

      if (!cloud_name) {
        throw new Error('Cloudinary não configurado');
      }

      setProgress(30);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', api_key);
      formData.append('timestamp', timestamp.toString());
      formData.append('signature', signature);
      formData.append('folder', folder);

      const response = await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`, {
        method: 'POST',
        body: formData,
      });

      setProgress(80);

      if (!response.ok) {
        throw new Error('Falha no upload');
      }

      const result = await response.json();
      setProgress(100);
      
      const secureUrl = result.secure_url;
      setLocalPreview(secureUrl);
      onUpload(secureUrl);
      
      toast.success('Imagem enviada!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Erro ao enviar imagem');
      setLocalPreview(imageUrl);
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLocalPreview(null);
    onRemove();
  };

  const handleUploadClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  return (
    <div className="aspect-square relative">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />

      {localPreview ? (
        <div className="w-full h-full rounded-xl overflow-hidden border-2 border-slate-200 relative group">
          <img 
            src={localPreview} 
            alt="Preview" 
            className="w-full h-full object-cover"
          />
          {!uploading && (
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={handleUploadClick}
              >
                <Upload className="w-4 h-4 mr-1" />
                Trocar
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={handleRemoveClick}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
          {uploading && (
            <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center">
              <Loader2 className="w-6 h-6 text-white animate-spin mb-2" />
              <span className="text-white text-xs">{progress}%</span>
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={handleUploadClick}
          disabled={uploading}
          className="w-full h-full rounded-xl border-2 border-dashed border-slate-300 hover:border-primary hover:bg-primary/5 transition-colors flex flex-col items-center justify-center gap-2"
        >
          {uploading ? (
            <>
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
              <span className="text-xs text-muted-foreground">{progress}%</span>
            </>
          ) : (
            <>
              <Image className="w-8 h-8 text-slate-400" />
              <span className="text-xs text-muted-foreground">Clique para enviar</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

// Video upload component
const VideoUploadSlot = ({ videoUrl, onUpload, onRemove }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [localPreview, setLocalPreview] = useState(videoUrl);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setLocalPreview(videoUrl);
  }, [videoUrl]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 100MB');
      return;
    }

    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!validTypes.includes(file.type)) {
      toast.error('Tipo de arquivo não suportado');
      return;
    }

    setUploading(true);
    setProgress(10);

    try {
      const sigResponse = await api.get('/cloudinary/signature?resource_type=video&folder=products');
      const { signature, timestamp, cloud_name, api_key, folder } = sigResponse.data;

      if (!cloud_name) {
        throw new Error('Cloudinary não configurado');
      }

      setProgress(30);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', api_key);
      formData.append('timestamp', timestamp.toString());
      formData.append('signature', signature);
      formData.append('folder', folder);

      const response = await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/video/upload`, {
        method: 'POST',
        body: formData,
      });

      setProgress(80);

      if (!response.ok) {
        throw new Error('Falha no upload');
      }

      const result = await response.json();
      setProgress(100);
      
      const secureUrl = result.secure_url;
      setLocalPreview(secureUrl);
      onUpload(secureUrl);
      
      toast.success('Vídeo enviado!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Erro ao enviar vídeo');
      setLocalPreview(videoUrl);
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLocalPreview(null);
    onRemove();
  };

  return (
    <div className="aspect-video relative max-w-md">
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />

      {localPreview ? (
        <div className="w-full h-full rounded-xl overflow-hidden border-2 border-slate-200 relative bg-black">
          <video src={localPreview} className="w-full h-full object-cover" controls />
          <Button
            type="button"
            size="icon"
            variant="destructive"
            className="absolute top-2 right-2"
            onClick={handleRemoveClick}
            disabled={uploading}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full h-full rounded-xl border-2 border-dashed border-slate-300 hover:border-primary hover:bg-primary/5 transition-colors flex flex-col items-center justify-center gap-2 min-h-[160px]"
        >
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <span className="text-sm text-muted-foreground">Enviando... {progress}%</span>
            </>
          ) : (
            <>
              <Video className="w-10 h-10 text-slate-400" />
              <span className="text-sm text-muted-foreground">Clique para enviar vídeo</span>
              <span className="text-xs text-muted-foreground">MP4, WebM, MOV (máx. 100MB)</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Use object with unique IDs for images instead of array
  const [imageSlots, setImageSlots] = useState([]);
  const [videoUrl, setVideoUrl] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    category_id: '',
    stock: '',
    min_stock: '5',
    is_kit: false,
    featured: false,
    active: true,
  });

  const loadProducts = async () => {
    try {
      setLoading(true);
      const params = search ? { search } : {};
      const response = await api.get('/products', { params });
      setProducts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadProducts();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const generateSlotId = () => `slot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const handleOpenDialog = (product = null) => {
    if (product) {
      setSelectedProduct(product);
      setFormData({
        name: product.name,
        description: product.description || '',
        price: product.price.toString(),
        original_price: product.original_price?.toString() || '',
        category_id: product.category_id,
        stock: product.stock.toString(),
        min_stock: product.min_stock?.toString() || '5',
        is_kit: product.is_kit,
        featured: product.featured,
        active: product.active,
      });
      // Convert existing images to slots with unique IDs
      const slots = (product.images || []).map(url => ({
        id: generateSlotId(),
        url: url
      }));
      setImageSlots(slots);
      setVideoUrl(product.video_url || '');
    } else {
      setSelectedProduct(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        original_price: '',
        category_id: '',
        stock: '',
        min_stock: '5',
        is_kit: false,
        featured: false,
        active: true,
      });
      // Start with one empty slot
      setImageSlots([{ id: generateSlotId(), url: '' }]);
      setVideoUrl('');
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validImages = imageSlots.filter(slot => slot.url).map(slot => slot.url);
    
    if (validImages.length === 0) {
      toast.error('Adicione pelo menos uma imagem');
      return;
    }

    const data = {
      name: formData.name,
      description: formData.description,
      price: parseFloat(formData.price),
      original_price: formData.original_price ? parseFloat(formData.original_price) : null,
      category_id: formData.category_id,
      images: validImages,
      video_url: videoUrl || null,
      stock: parseInt(formData.stock),
      min_stock: parseInt(formData.min_stock),
      is_kit: formData.is_kit,
      featured: formData.featured,
      active: formData.active,
    };

    try {
      if (selectedProduct) {
        await api.put(`/products/${selectedProduct.id}`, data);
        toast.success('Produto atualizado!');
      } else {
        await api.post('/products', data);
        toast.success('Produto criado!');
      }
      setDialogOpen(false);
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar produto');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/products/${selectedProduct.id}`);
      toast.success('Produto removido!');
      setDeleteDialogOpen(false);
      loadProducts();
    } catch (error) {
      toast.error('Erro ao remover produto');
    }
  };

  const handleImageUpload = useCallback((slotId, url) => {
    setImageSlots(prev => prev.map(slot => 
      slot.id === slotId ? { ...slot, url } : slot
    ));
  }, []);

  const handleImageRemove = useCallback((slotId) => {
    setImageSlots(prev => {
      const filtered = prev.filter(slot => slot.id !== slotId);
      // Keep at least one slot
      if (filtered.length === 0) {
        return [{ id: generateSlotId(), url: '' }];
      }
      return filtered;
    });
  }, []);

  const addImageSlot = useCallback(() => {
    if (imageSlots.length < 5) {
      setImageSlots(prev => [...prev, { id: generateSlotId(), url: '' }]);
    }
  }, [imageSlots.length]);

  return (
    <AdminLayout title="Produtos">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar produtos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-xl"
            data-testid="products-search"
          />
        </div>
        <Button onClick={() => handleOpenDialog()} className="rounded-xl" data-testid="add-product-button">
          <Plus className="w-4 h-4 mr-2" />
          Novo Produto
        </Button>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="admin-table" data-testid="products-table">
            <thead>
              <tr>
                <th>Produto</th>
                <th>Categoria</th>
                <th>Preço</th>
                <th>Estoque</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-muted-foreground">
                    Carregando...
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-muted-foreground">
                    Nenhum produto encontrado
                  </td>
                </tr>
              ) : (
                products.map((product) => {
                  const category = categories.find(c => c.id === product.category_id);
                  return (
                    <tr key={product.id} data-testid={`product-row-${product.id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                            {product.images?.[0] ? (
                              <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-5 h-5 text-slate-400" />
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{product.name}</p>
                            <div className="flex gap-1">
                              {product.is_kit && (
                                <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">KIT</span>
                              )}
                              {product.featured && (
                                <span className="text-xs bg-secondary/20 text-secondary-foreground px-1.5 py-0.5 rounded">Destaque</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{category?.name || '-'}</td>
                      <td>
                        <p className="font-medium">{formatCurrency(product.price)}</p>
                        {product.original_price && (
                          <p className="text-xs text-muted-foreground line-through">
                            {formatCurrency(product.original_price)}
                          </p>
                        )}
                      </td>
                      <td>
                        <span className={`font-medium ${product.stock <= product.min_stock ? 'text-destructive' : ''}`}>
                          {product.stock}
                        </span>
                        {product.stock <= product.min_stock && (
                          <p className="text-xs text-destructive">Estoque baixo</p>
                        )}
                      </td>
                      <td>
                        <span className={`status-badge ${product.active ? 'status-paid' : 'status-cancelled'}`}>
                          {product.active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              setSelectedProduct(product);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Product Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedProduct ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Images Upload Section */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Imagens do Produto *</Label>
              <p className="text-sm text-muted-foreground">
                Envie até 5 imagens. A primeira será a imagem principal.
              </p>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {imageSlots.map((slot) => (
                  <ImageUploadSlot
                    key={slot.id}
                    slotId={slot.id}
                    imageUrl={slot.url}
                    onUpload={(url) => handleImageUpload(slot.id, url)}
                    onRemove={() => handleImageRemove(slot.id)}
                  />
                ))}
                
                {imageSlots.length < 5 && (
                  <button
                    type="button"
                    onClick={addImageSlot}
                    className="aspect-square rounded-xl border-2 border-dashed border-slate-300 hover:border-primary hover:bg-primary/5 transition-colors flex flex-col items-center justify-center gap-2"
                    data-testid="add-image-slot"
                  >
                    <ImagePlus className="w-6 h-6 text-slate-400" />
                    <span className="text-xs text-muted-foreground">Adicionar</span>
                  </button>
                )}
              </div>
            </div>

            {/* Video Upload Section */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Vídeo Promocional (opcional)</Label>
              <p className="text-sm text-muted-foreground">
                Envie um vídeo de apresentação do produto.
              </p>
              
              <VideoUploadSlot
                videoUrl={videoUrl}
                onUpload={setVideoUrl}
                onRemove={() => setVideoUrl('')}
              />
            </div>

            <div className="border-t pt-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do Produto *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: Kit Básico Família"
                    required
                    data-testid="product-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="category">Categoria *</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}
                  >
                    <SelectTrigger data-testid="product-category-select">
                      <SelectValue placeholder="Selecione uma categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição do Card</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descrição que aparecerá no card do produto..."
                  rows={3}
                  data-testid="product-description-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Preço de Venda *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">R$</span>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      className="pl-10"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="0,00"
                      required
                      data-testid="product-price-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="original_price">Preço Original (para desconto)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">R$</span>
                    <Input
                      id="original_price"
                      type="number"
                      step="0.01"
                      className="pl-10"
                      value={formData.original_price}
                      onChange={(e) => setFormData(prev => ({ ...prev, original_price: e.target.value }))}
                      placeholder="0,00"
                      data-testid="product-original-price-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="stock">Quantidade em Estoque *</Label>
                  <Input
                    id="stock"
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value }))}
                    placeholder="0"
                    required
                    data-testid="product-stock-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="min_stock">Estoque Mínimo (alerta)</Label>
                  <Input
                    id="min_stock"
                    type="number"
                    value={formData.min_stock}
                    onChange={(e) => setFormData(prev => ({ ...prev, min_stock: e.target.value }))}
                    placeholder="5"
                    data-testid="product-min-stock-input"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-6 pt-4 border-t">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={formData.is_kit}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_kit: checked }))}
                    data-testid="product-is-kit-switch"
                  />
                  <div>
                    <Label className="cursor-pointer">É um Kit</Label>
                    <p className="text-xs text-muted-foreground">Marque se é um combo de produtos</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Switch
                    checked={formData.featured}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, featured: checked }))}
                    data-testid="product-featured-switch"
                  />
                  <div>
                    <Label className="cursor-pointer">Destaque</Label>
                    <p className="text-xs text-muted-foreground">Aparece em destaque no feed</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Switch
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active: checked }))}
                    data-testid="product-active-switch"
                  />
                  <div>
                    <Label className="cursor-pointer">Ativo</Label>
                    <p className="text-xs text-muted-foreground">Visível na loja</p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-product-button">
                {selectedProduct ? 'Salvar Alterações' : 'Publicar Produto'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Produto</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover "{selectedProduct?.name}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminProducts;
